import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useCopyToClipboard } from 'src/hooks/useCopyToClipboard'

interface Props {
  title: string
  value: string
  /** Snackbar text after a successful copy, e.g. "Subscription ID copied". */
  copiedMessage: string
  copyAriaLabel: string
  dataTestId?: string
  /** Defaults to `${dataTestId}-copy-button` when `dataTestId` is set. */
  copyButtonDataTestId?: string
  className?: any
}

/** A read-only ID as a `DataRow`, with a copy button pinned to its right edge. */
const CopyableIdRowComponent = ({
  title,
  value,
  copiedMessage,
  copyAriaLabel,
  dataTestId,
  copyButtonDataTestId,
  className,
}: Props) => {
  const copyToClipboard = useCopyToClipboard()

  const handleCopy = useCallback(() => {
    copyToClipboard(value, copiedMessage)
  }, [copiedMessage, copyToClipboard, value])

  return (
    <DataRow
      title={title}
      direction="column"
      text={value}
      className={className}
      textClassName={tw`break-all`}
      dataTestId={dataTestId}
      action={
        <Tooltip title="Copy">
          <span>
            <Button
              size="small"
              color="grey"
              iconOnly
              leftIcon={
                <GSIcon iconName="gs_square_behind_square_1" size={20} css={tw`text-shades-1000`} />
              }
              aria-label={copyAriaLabel}
              onClick={handleCopy}
              data-testid={
                copyButtonDataTestId ?? (dataTestId ? `${dataTestId}-copy-button` : undefined)
              }
            />
          </span>
        </Tooltip>
      }
    />
  )
}

export const CopyableIdRow = memo(CopyableIdRowComponent)
